import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { SalesProductSummary, listSalesProducts } from '../../services/salesProductsApi';
import {
  createCampaignTag,
  deleteCampaignTag,
  getCampaignProducts,
  listCampaignTags,
  ProspectCampaignTag,
  replaceCampaignProducts,
  updateCampaignTag,
} from '../../services/campaignTagsApi';

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('pt-BR');
}

export function CampaignTagsPage() {
  const { canWriteModule, canReadModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('sales.campaign-tags');
  const canManageProducts = canReadModule('sales.products');
  const [tags, setTags] = useState<ProspectCampaignTag[]>([]);
  const [activeProducts, setActiveProducts] = useState<SalesProductSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const tagResult = await listCampaignTags({ page: targetPage, pageSize });
      setTags(tagResult.items);
      setTotalCount(tagResult.totalCount);
      setPage(tagResult.page);

      if (canManageProducts) {
        const productResult = await listSalesProducts({ activeOnly: true, page: 1, pageSize: 200 });
        setActiveProducts(productResult.items);
      } else {
        setActiveProducts([]);
      }
    } catch {
      toast.error('Falha ao carregar campanhas.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageProducts, page]);

  function resetForm(): void {
    setEditingId(null);
    setName('');
    setSelectedProductIds(new Set());
  }

  async function startEdit(tag: ProspectCampaignTag): Promise<void> {
    expandForm();
    setEditingId(tag.id);
    setName(tag.name);
    if (!canManageProducts) {
      setSelectedProductIds(new Set());
      return;
    }

    try {
      const products = await getCampaignProducts(tag.id);
      setSelectedProductIds(new Set(products.map((product) => product.id)));
    } catch {
      toast.error('Falha ao carregar produtos da campanha.');
      setSelectedProductIds(new Set());
    }
  }

  function toggleProduct(productId: string): void {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Informe o nome da campanha.');
      return;
    }

    try {
      let campaignId = editingId;
      if (editingId) {
        await updateCampaignTag(editingId, trimmed);
        toast.success('Campanha atualizada.');
      } else {
        const created = await createCampaignTag(trimmed);
        campaignId = created.id;
        toast.success('Campanha criada.');
      }

      if (campaignId && canManageProducts) {
        await replaceCampaignProducts(campaignId, Array.from(selectedProductIds));
      }

      if (editingId) {
        resetForm();
      } else {
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error(editingId ? 'Falha ao atualizar campanha.' : 'Falha ao criar campanha. Verifique se o nome já existe.');
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (!canWrite) return;
    if (
      !(await confirm({
        title: 'Excluir campanha',
        message: 'Excluir esta campanha? Prospects já vinculados manterão o nome gravado.',
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }

    try {
      await deleteCampaignTag(id);
      toast.success('Campanha excluída.');
      if (editingId === id) {
        collapseForm(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir campanha.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Campanhas de prospecção"
        description="Tags usadas na extensão Chrome e nas importações de leads. Associe zero ou mais produtos a cada campanha."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar campanha' : 'Nova campanha'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId ? `Editando ${name || 'campanha selecionada'} — clique para expandir` : undefined
          }
          createSummary="Clique para expandir e cadastrar uma nova campanha"
          className="mb-6"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="px-4 pb-4 pt-3">
          <div className="space-y-4">
          <label className="erp-label">
            Nome
            <input
              className="erp-input"
              placeholder="Ex.: Outbound Q2 2026"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              required
            />
          </label>

          {canManageProducts ? (
          <div>
            <p className="text-sm font-medium text-secondary-800">Produtos associados</p>
            <p className="text-xs text-secondary-500 mb-2">Selecione nenhum, um ou vários produtos ativos.</p>
            {activeProducts.length === 0 ? (
              <p className="text-sm text-secondary-500">Nenhum produto ativo cadastrado.</p>
            ) : (
              <div className="grid gap-2 md:grid-cols-2">
                {activeProducts.map((product) => (
                  <label key={product.id} className="flex items-center gap-2 rounded-lg border border-secondary-200 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selectedProductIds.has(product.id)}
                      onChange={() => toggleProduct(product.id)}
                    />
                    {product.logoBase64 ? (
                      <img src={product.logoBase64} alt="" className="h-6 w-6 object-contain" />
                    ) : null}
                    <span>{product.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          ) : null}

          <FormActionBar
            onCancel={() => collapseForm(resetForm)}
            cancelLabel="Cancelar"
          >
            <button type="submit" className="erp-btn-primary">Salvar</button>
          </FormActionBar>
          </div>
        </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="erp-table-wrap">
        {loading ? (
          <p className="erp-loading">Carregando...</p>
        ) : tags.length === 0 ? (
          <p className="erp-empty">Nenhuma campanha cadastrada.</p>
        ) : (
          <div className="erp-table-scroll">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Criada em</th>
                <th>Atualizada em</th>
                {canWrite ? <th>Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {tags.map((tag) => (
                <tr key={tag.id}>
                  <td className="font-medium text-secondary-900">{tag.name}</td>
                  <td>{formatDate(tag.createdAtUtc)}</td>
                  <td>{formatDate(tag.updatedAtUtc)}</td>
                  {canWrite ? (
                    <td>
                      <div className="flex items-center gap-1">
                        <EditIconButton label="Editar campanha" onClick={() => void startEdit(tag)} />
                        <DeleteIconButton label="Excluir campanha" onClick={() => void handleDelete(tag.id)} />
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      <ListPaginationBar
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
      />
    </PageShell>
  );
}
